import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Mail, MapPin } from 'lucide-react';
// import { Phone } from 'lucide-react';
// import { Send, CheckCircle, Building2, Users, Briefcase } from 'lucide-react'; // Commented out - form not used

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  // const formRef = useRef<HTMLFormElement>(null); // Commented out - form not used
  const infoRef = useRef<HTMLDivElement>(null);
  
  // Form state commented out - form not used
  // const [formData, setFormData] = useState({
  //   name: '',
  //   email: '',
  //   company: '',
  //   industry: '',
  //   message: '',
  // });
  // const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    
    // Skip ScrollTrigger animations on mobile for better performance
    if (isMobile) {
      // Just set elements to visible state immediately on mobile
      if (titleRef.current) {
        gsap.set(titleRef.current, { opacity: 1, y: 0 });
      }
      if (infoRef.current) {
        gsap.set(infoRef.current, { opacity: 1, x: 0 });
      }
      return;
    }
    
    const ctx = gsap.context(() => {
      // Set initial states first to prevent flashing
      if (titleRef.current) gsap.set(titleRef.current, { opacity: 1, y: 0 });
      if (infoRef.current) gsap.set(infoRef.current, { opacity: 1, x: 0 });
      
      // Title animation - desktop only
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 85%',
            once: true, // Only animate once - prevents flashing
            toggleActions: 'play none none none', // Never reverse
          },
        }
      );

      // Info animation - desktop only
      gsap.fromTo(
        infoRef.current,
        { opacity: 0, x: 50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.7,
          delay: 0.3,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 85%',
            once: true, // Only animate once - prevents flashing
            toggleActions: 'play none none none', // Never reverse
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Form handlers commented out - form not used
  // const handleSubmit = (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setIsSubmitted(true);
  //   setTimeout(() => {
  //     setIsSubmitted(false);
  //     setFormData({ name: '', email: '', company: '', industry: '', message: '' });
  //   }, 3000);
  // };

  // const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
  //   setFormData({ ...formData, [e.target.name]: e.target.value });
  // };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'info@kmgtechnologies.com',
      href: 'mailto:info@kmgtechnologies.com',
    },
    // {
    //   icon: Phone,
    //   label: 'Phone',
    //   value: '+1 (514) 710-2555',
    //   href: 'tel:+15147102555',
    // },
    {
      icon: MapPin,
      label: 'Location',
      value: '5579 rue Paré, Montreal',
      href: '#',
    },
  ];

  // Industries list commented out - form not used
  // const industries = [
  //   'Finance',
  //   'Healthcare',
  //   'Retail',
  //   'Manufacturing',
  //   'Technology',
  //   'Logistics',
  //   'Other',
  // ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-32 bg-kmg-bg overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div ref={titleRef} className="text-center mb-16">
          <span className="inline-block px-4 py-2 text-sm font-medium text-kmg-primary bg-kmg-primary/10 rounded-full mb-6">
            Get Started
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Let's <span className="text-gradient">Talk Data</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Schedule a free data assessment. We'll show you exactly how much value is hiding in your data.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Contact Form - Commented out */}
          {/* <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="relative p-8 bg-white rounded-2xl shadow-card"
          >
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-kmg-primary focus:ring-1 focus:ring-kmg-primary transition-all duration-300"
                      placeholder="John Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Work Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-kmg-primary focus:ring-1 focus:ring-kmg-primary transition-all duration-300"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-kmg-primary focus:ring-1 focus:ring-kmg-primary transition-all duration-300"
                      placeholder="Your Company"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Industry
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-kmg-primary focus:ring-1 focus:ring-kmg-primary transition-all duration-300 appearance-none"
                    >
                      <option value="">Select Industry</option>
                      {industries.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tell Us About Your Data Challenges
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-kmg-primary focus:ring-1 focus:ring-kmg-primary transition-all duration-300 resize-none"
                  placeholder="What data challenges are you facing? What would you like to achieve?"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitted}
                className="group w-full flex items-center justify-center gap-3 px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-kmg-primary to-kmg-accent rounded-xl hover:shadow-glow transition-all duration-300 ease-expo-out hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitted ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Request Sent! We'll be in touch.
                  </>
                ) : (
                  <>
                    Request Free Data Assessment
                    <Send className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>

              <p className="text-center text-sm text-slate-500">
                Free assessment • No commitment • Results in 48 hours
              </p>
            </div>
          </form> */}

          {/* Contact Info */}
          <div ref={infoRef} className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Contact Information
              </h3>
              <p className="text-slate-600">
                Reach out to us directly. Our data specialists 
                are ready to help you discover the value in your data.
              </p>
            </div>

            <div className="space-y-4">
              {contactInfo.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    className="group flex items-center gap-4 p-4 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-kmg-primary/10 rounded-xl group-hover:bg-kmg-primary/20 transition-colors duration-300">
                      <Icon className="w-5 h-5 text-kmg-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{item.label}</p>
                      <p className="text-slate-900 font-medium group-hover:text-kmg-primary transition-colors duration-300">
                        {item.value}
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>

            {/* Office Hours */}
            <div className="p-6 bg-gradient-to-br from-kmg-primary/10 to-kmg-accent/10 rounded-xl border border-kmg-primary/10">
              <h4 className="text-lg font-semibold text-slate-900 mb-3">
                Response Time
              </h4>
              <div className="space-y-2 text-slate-600">
                <p>Initial response: Within 4 hours</p>
                <p>Data assessment: 48 hours</p>
                <p>Full proposal: 5 business days</p>
              </div>
            </div>

            {/* Trust Badge */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
